import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import {
  useLocation,
  useNavigationType,
  useSearchParams,
  useParams,
} from "react-router-dom"

import { setExperiment } from "app-store-v2/app/actions"
import { PRODUCT_REVIEWS } from "areas/product/jewlr/wizard-header/pdp-reviews-carousel"
import {
  clearDiamondTmpData,
  fetchStylecode,
  loadWizard,
  productFound,
  removeChoice,
  setWizardLoading,
  setExtraWizardParams,
  showMiniview,
  updateImages,
} from "areas/product/store/actions"
import { IS_GE_CHECKOUT, isTestMode } from "helpers/application"
import { isJ3dView, isVideoView } from "helpers/gallery-slide-view"

const productSchemaImages = (previewImageData) => {
  const images = previewImageData?.images || {}
  const imageUrls = (previewImageData?.all_views || [])
    .filter((view) => !isVideoView(view) && !isJ3dView(view))
    .map((view) => images[`${view}_view`] || images[view])
    .map((slide) => slide?.src)
    .filter(Boolean)

  return imageUrls
}

export default function fetchProduct(WrappedComponent) {
  const FetchProduct = ({ share, ...props }) => {
    const location = useLocation()
    const action = useNavigationType()
    const [searchParams] = useSearchParams()
    const { styleCode: paramStyleCode } = useParams()
    const is_ge_checkout = IS_GE_CHECKOUT()

    const currentHash = useRef(location.hash)

    const dispatch = useDispatch()
    const {
      adminMode,
      choices,
      experiment,
      featuredReviews,
      formUrl,
      olid,
      pricing,
      productDescription,
      productReviews,
      productType,
      previewImageData,
      query_string,
      saleEndsIso,
      styleCode,
      wizard,
    } = useSelector(
      (state) => ({
        adminMode: state.wizard.adminMode,
        choices: state.choices,
        experiment: state.experiment,
        featuredReviews: state.wizard.pdpFeaturedReviews,
        formUrl: state.wizard.formUrl,
        olid: state.wizard.olid,
        previewImageData: state.previewImage.data,
        pricing: state.wizard.price_and_promo,
        productDescription: state.wizard.loading
          ? ""
          : state.wizard.description.product_description,
        productReviews: state.wizard.productReviews,
        productType: state.wizard.description.product_type,
        query_string: state.wizard.query_string,
        saleEndsIso: state.wizard.price_and_promo.sale_ends_iso,
        styleCode: state.wizard.description.style_code,
        wizard: state.wizard,
      }),
      shallowEqual
    )

    const styleCodeMatch = styleCode === paramStyleCode

    const loadProduct = () => {
      const ssr = action === "POP" && styleCodeMatch

      if (!ssr) {
        dispatch(clearDiamondTmpData())
        dispatch(setWizardLoading("full"))

        // On new product load, remove lid to allow add to cart
        dispatch(removeChoice("lid"))
        dispatch(setExtraWizardParams({ lid: "" }))
      }

      // load new product
      const search = searchParams.toString()
      if (
        styleCodeMatch &&
        (search === "" || search === query_string || formUrl)
      ) {
        dispatch(
          loadWizard({
            adminMode,
            olid,
            style_code: styleCode,
          })
        )
      } else {
        // Reset image slider to prevent previous loaded images from showing
        dispatch(
          updateImages({
            data: { all_views: [], images: {} },
          })
        )
        dispatch(showMiniview(false))
        dispatch(fetchStylecode(paramStyleCode, true))
      }
    }

    // eslint-disable-next-line quotes
    const productTitle = wizard.title
    const productUrl = `https://${sl.config.site_url}${wizard.base_product_url}`
    const canonicalUrl =
      wizard.header?.canonical_permalink &&
      `https://${sl.config.site_url}/products/${styleCode}/${wizard.header.canonical_permalink}`

    const skuData = wizard.data?.sku || wizard.default_sku
    const selectedSkuOption =
      !wizard.default_sku &&
      wizard.data?.sku?.options?.find(
        (opt) => opt.value === choices?.sku?.value
      )
    const productImages = productSchemaImages(previewImageData)
    const imageSchema =
      productImages.length > 0 ? productImages : wizard.description?.product_img
    const productVideoMeta = previewImageData?.images?.video_0?.meta

    let breadcrumbList = []
    let productVideoSchema = null

    if (productVideoMeta) {
      /* eslint-disable sort-keys */
      productVideoSchema = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: productVideoMeta.name,
        description: productVideoMeta.description,
        contentUrl: productVideoMeta.content_url,
        thumbnailUrl: productVideoMeta.thumbnail_url,
        duration: productVideoMeta.duration,
        uploadDate: productVideoMeta.upload_date,
      }
      /* eslint-enable sort-keys */
    }

    const productReview = featuredReviews?.length
      ? featuredReviews[0]
      : PRODUCT_REVIEWS[styleCode]?.length
        ? PRODUCT_REVIEWS[styleCode][0]
        : null
    const reviewSchema = productReview && {
      "@type": "Review",
      author: {
        "@type": "Person",
        name: productReview.name,
      },
      reviewBody: productReview.text,
      reviewRating: {
        "@type": "Rating",
        ratingValue: "5",
      },
    }

    const deliveryTimeSchema = (wizard.header?.min_manufacturing_days ||
      wizard.header?.min_transit_days) && {
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        ...(wizard.header.min_manufacturing_days && {
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: wizard.header?.min_manufacturing_days,
            unitCode: "DAY",
          },
        }),
        ...(wizard.header.min_transit_days && {
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: wizard.header?.min_transit_days,
            unitCode: "DAY",
          },
        }),
      },
    }

    /* eslint-disable sort-keys */
    const aggregateRatingSchema = productReviews?.count && {
      "@type": "AggregateRating",
      ratingValue: productReviews.average_rating,
      reviewCount: productReviews.count,
      bestRating: 5,
    }
    /* eslint-enable sort-keys */

    if (wizard.description.breadcrumb) {
      breadcrumbList = wizard.description.breadcrumb.map((product, index) => ({
        "@type": "ListItem",
        item: {
          "@id": `https://${sl.config.site_url}${product.url}`,
          name: product.name,
        },
        position: index + 1,
      }))
      breadcrumbList.push({
        "@type": "ListItem",
        item: {
          "@id": productUrl,
          name: productTitle,
        },
        position: wizard.description.breadcrumb.length + 1,
      })
    }

    useEffect(() => {
      if (
        !isTestMode &&
        !wizard.loading &&
        styleCodeMatch &&
        Object.keys(wizard.data).length > 0
      ) {
        let itemSku = choices?.sku?.value || skuData?.options?.[0]?.value
        let itemVariant = itemSku
          ? `${paramStyleCode}_${itemSku}`
          : paramStyleCode
        let itemCategory = wizard.productCategory

        let variant = skuData?.options?.find((op) => op.value == itemSku)
        let productBasePrice =
          variant?.display_price ||
          skuData?.options?.[0]?.display_price ||
          skuData?.options?.[0]?.sale_price ||
          skuData?.options?.[0]?.price ||
          0

        dataLayer.push({
          contentIds: [paramStyleCode],
          contentType: "product",
          currencyCode: sl.config.currency_code,
          ecommerce: {
            detail: {
              products: [
                {
                  name: paramStyleCode,
                },
              ],
            },
          },
          event: "viewed-product",
          ga4Items: [
            {
              currency: sl.config.currency_code,
              item_category: itemCategory,
              item_id: itemVariant,
              item_name: paramStyleCode,
              item_variant: itemSku,
              quantity: 1,
            },
          ],
          productBasePrice: productBasePrice,
          productCategory: itemCategory,
          productId: paramStyleCode,
          productImage:
            wizard?.description?.product_default_img ||
            wizard?.description?.product_img,
          productTitle: wizard?.seo_title,
          productUrl,
          viewProductUniqueEventId:
            window.cache_free_ssr_data.unique_event_ids.pdp,
        })

        let primaryTag = wizard?.primaryTag
        if (primaryTag) {
          dataLayer.push({
            event: "viewed-product-primary-tag",
            productPrimaryTag: primaryTag,
          })
        }
      }
    }, [wizard.loading])

    useEffect(() => {
      if (
        !isTestMode &&
        !wizard.loading &&
        Object.keys(wizard.data).length > 0 &&
        paramStyleCode &&
        choices?.sku?.icon_text
      ) {
        let itemStyleCode = choices?.style_code?.value
        let exp_key = `PPT_${itemStyleCode}`
        if (itemStyleCode && experiment[exp_key]) {
          dispatch(
            setExperiment({
              [exp_key]: experiment[exp_key],
            })
          )
        }
      }
    }, [wizard.loading, choices?.sku?.icon_text])

    useEffect(() => {
      if (action !== "REPLACE" || currentHash.current !== location.hash) {
        loadProduct()
        currentHash.current = location.hash
      }
      return () => {
        dispatch(productFound())
      }
    }, [paramStyleCode])

    return (
      <>
        {!wizard.loading && skuData && typeof skuData === "object" && (
          <Helmet encodeSpecialCharacters={false}>
            {/* eslint-disable sort-keys */}
            <title>{wizard?.seo_title}</title>
            {wizard.noindex && <meta content="noindex" name="robots" />}
            <meta content={wizard?.seo_title} name="og:title" />
            <meta content="og:product" property="og:type" />
            <meta content={productDescription} name="description" />
            <meta content={productDescription} property="og:description" />
            <meta
              content={share ? wizard?.shareUrl : productUrl}
              id="og_url"
              property="og:url"
            />
            <meta
              content={wizard?.price_and_promo?.price_to_display?.toFixed(2)}
              property="og:price:amount"
            />
            <meta
              content={sl.config.currency_code}
              property="og:price:currency"
            />
            <meta
              content={wizard.header?.product_out_of_stock ? "oos" : "instock"}
              property="og:availability"
            />
            <meta
              content={wizard.description.style_code}
              property="style_code"
            />
            {canonicalUrl && <link href={canonicalUrl} rel="canonical" />}
            {(wizard.header?.hreflangs || []).map(
              ({ country, locale, url }) => (
                <link
                  href={`${sl.config.site_url_by_country[country]}${url}`}
                  hrefLang={locale}
                  key={country}
                  rel="alternate"
                />
              )
            )}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org/",
                "@type": "Product",
                name: productTitle,
                url: productUrl,
                image: imageSchema,
                description: productDescription,
                productID: wizard.description.style_code,
                sku: wizard.description.style_code,
                mpn: wizard.description.style_code,
                brand: {
                  "@type": "Brand",
                  name: sl.config.site_title,
                },
                ...(selectedSkuOption && {
                  material:
                    selectedSkuOption.full_title || selectedSkuOption.title,
                  ...(selectedSkuOption.schema_color && {
                    color: selectedSkuOption.schema_color,
                  }),
                }),
                ...(wizard?.data?.sz?.schema_size && {
                  size: wizard.data.sz.schema_size,
                }),
                offers: [
                  {
                    "@type": "Offer",
                    url: productUrl,
                    priceCurrency: sl.config.currency_code,
                    price:
                      pricing.price_to_display ||
                      skuData.options?.[0]?.display_price ||
                      skuData.options?.[0]?.sale_price ||
                      skuData.options?.[0]?.price,
                    ...(saleEndsIso && {
                      priceValidUntil: saleEndsIso,
                    }),
                    availability: `https://schema.org/${
                      wizard.header?.product_out_of_stock
                        ? "OutOfStock"
                        : "InStock"
                    }`,
                    itemCondition: "https://schema.org/NewCondition",
                    seller: {
                      "@type": "Organization",
                      name: sl.config.site_title,
                    },
                    shippingDetails: {
                      "@type": "OfferShippingDetails",
                      shippingDestination: {
                        "@type": "DefinedRegion",
                        addressCountry: sl.config.site_country || "US",
                      },
                      shippingRate: {
                        "@type": "MonetaryAmount",
                        value: "0.00",
                        currency: sl.config.currency_code,
                      },
                      ...deliveryTimeSchema,
                    },
                    ...(productType !== "ACCESSORY" && {
                      hasMerchantReturnPolicy: {
                        "@type": "MerchantReturnPolicy",
                        returnPolicyCategory:
                          "https://schema.org/MerchantReturnFiniteReturnWindow",
                        applicableCountry: sl.config.site_country || "US",
                        merchantReturnDays: is_ge_checkout ? 30 : 99,
                        merchantReturnLink: `https://${sl.config.site_url}/order/return`,
                        itemCondition: "https://schema.org/NewCondition",
                        returnMethod: "https://schema.org/ReturnByMail",
                        returnFees: "https://schema.org/FreeReturn",
                        refundType: "https://schema.org/FullRefund",
                        returnLabelSource:
                          "https://schema.org/ReturnLabelCustomerResponsibility",
                      },
                    }),
                  },
                ],
                ...(aggregateRatingSchema && {
                  aggregateRating: aggregateRatingSchema,
                }),
                ...(reviewSchema && { review: reviewSchema }),
              })}
            </script>

            {breadcrumbList.length > 0 && (
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: breadcrumbList,
                })}
              </script>
            )}

            {productVideoSchema && (
              <script type="application/ld+json">
                {JSON.stringify(productVideoSchema)}
              </script>
            )}
            {/* eslint-enable sort-keys */}
          </Helmet>
        )}
        <WrappedComponent {...props} />
      </>
    )
  }

  FetchProduct.propTypes = {
    share: PropTypes.bool,
  }

  return FetchProduct
}
